import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { IgListSkeleton } from "components/InstagramLoader";
import Pagination from "@mui/material/Pagination";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableSortLabel from "@mui/material/TableSortLabel";
import LinearProgress from "@mui/material/LinearProgress";
import { alpha, useTheme } from "@mui/material/styles";

import SchoolIcon from "@mui/icons-material/School";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Close";
import AppleIcon from "@mui/icons-material/Apple";
import AndroidIcon from "@mui/icons-material/Android";
import VerifiedIcon from "@mui/icons-material/Verified";
import GroupsIcon from "@mui/icons-material/Groups";
import InfoIcon from "@mui/icons-material/InfoOutlined";

import { GetUniversityInsightsCall } from "../redux/api/report.api";

const PAGE_SIZE = 25;

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
}

function formatNumber(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "0";
  return v.toLocaleString();
}

/* Compact bar showing the iOS / Android split for a single bucket. */
function PlatformBar({ ios, android, total }) {
  const theme = useTheme();
  const t = Math.max(0, Number(total) || 0);
  const i = Math.max(0, Number(ios) || 0);
  const a = Math.max(0, Number(android) || 0);
  const iosPct = t > 0 ? Math.round((i / t) * 100) : 0;
  const androidPct = t > 0 ? Math.round((a / t) * 100) : 0;
  const otherPct = Math.max(0, 100 - iosPct - androidPct);

  return (
    <Box sx={{ minWidth: 140 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <AppleIcon sx={{ fontSize: 14, color: "text.primary" }} />
          <Typography variant="caption" color="text.secondary">
            {i}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <AndroidIcon sx={{ fontSize: 14, color: "success.main" }} />
          <Typography variant="caption" color="text.secondary">
            {a}
          </Typography>
        </Stack>
      </Stack>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: 6,
          borderRadius: 999,
          overflow: "hidden",
          bgcolor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.common.white, 0.08)
              : alpha(theme.palette.common.black, 0.06),
        }}
      >
        <Box sx={{ width: `${iosPct}%`, bgcolor: theme.palette.text.primary }} />
        <Box sx={{ width: `${androidPct}%`, bgcolor: theme.palette.success.main }} />
        <Box sx={{ width: `${otherPct}%` }} />
      </Box>
    </Box>
  );
}

/* Quick-filter chips shown on top of the table. Each entry maps a UI label
   to a backend `filter` value understood by getUniversityInsights. */
const UNI_QUICK_FILTERS = [
  { id: "all", label: "All" },
  { id: "linked", label: "Linked only" },
  { id: "freetext", label: "Free-text only" },
  { id: "subscribers", label: "With subscribers" },
  { id: "multi", label: "Multiple spellings" },
];

/* Sortable columns. Map UI key → backend `sort` value. */
const UNI_SORT_KEYS = {
  users: "users",
  subscribers: "subscribers",
  linked: "linked",
  variants: "variants",
};

export default function AdminStatsUniversityInsights({ embedded = false }) {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(true);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("users");
  const [order, setOrder] = useState("desc");
  const [filter, setFilter] = useState("all");
  const debounceRef = useRef(null);

  // Debounce search input → server query.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await GetUniversityInsightsCall({
        page,
        limit: PAGE_SIZE,
        search,
        sort,
        order,
        filter,
      });
      if (cancelled) return;
      if (res && Array.isArray(res.items)) {
        setAuthorized(true);
        setData(res);
      } else {
        // Either a transport error or 401/403 (non-admin role): hide the section silently.
        setAuthorized(false);
        setData(null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [page, search, sort, order, filter]);

  /* Clicking a sortable column header: same column toggles direction, new
     column resets to "desc" so the user always lands on high→low first. */
  const handleSortClick = (key) => {
    if (sort === key) {
      setOrder((prev) => (prev === "desc" ? "asc" : "desc"));
    } else {
      setSort(key);
      setOrder("desc");
    }
    setPage(1);
  };

  const handleFilterClick = (id) => {
    setFilter(id);
    setPage(1);
  };

  const totalPages = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, Number(data.pages) || 1);
  }, [data]);

  const items = data?.items ?? [];

  // Hide the entire section for users who aren't admins (endpoint returns 403).
  if (!authorized && !loading) return null;

  return (
    <Box sx={{ mb: embedded ? 0 : 4 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", sm: "flex-end" }}
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        {!embedded ? (
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
              Universities
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Aggregated by free-text and linked university. Variations like
              “harvard”, “Uni Harvard”, “University of Harvard” are merged into a
              single bucket.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1 }} />
        )}
        <TextField
          size="small"
          placeholder="Search universities…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: { xs: "100%", sm: 280 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchInput("")}
                  aria-label="Clear search"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
        <Chip
          icon={<SchoolIcon />}
          label={`${formatNumber(data?.total_universities || 0)} unique universities`}
          variant="outlined"
          size="small"
          color="primary"
        />
        <Chip
          icon={<GroupsIcon />}
          label={`${formatNumber(data?.users_with_university || 0)} users with university`}
          variant="outlined"
          size="small"
        />
        <Chip
          icon={<InfoIcon />}
          label={`${formatNumber(data?.users_without_university || 0)} without`}
          variant="outlined"
          size="small"
          color="default"
        />
      </Stack>

      <Stack
        direction="row"
        flexWrap="wrap"
        gap={1}
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          Filter:
        </Typography>
        {UNI_QUICK_FILTERS.map((f) => {
          const active = filter === f.id;
          return (
            <Chip
              key={f.id}
              label={f.label}
              size="small"
              color={active ? "primary" : "default"}
              variant={active ? "filled" : "outlined"}
              onClick={() => handleFilterClick(f.id)}
              sx={{ fontWeight: active ? 600 : 500 }}
            />
          );
        })}
      </Stack>

      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          borderColor: alpha(theme.palette.divider, 0.9),
        }}
      >
        <CardHeader
          title={
            search
              ? `Search results for “${search}”`
              : `Top universities · page ${data?.page || 1} / ${totalPages}`
          }
          subheader={
            data
              ? `${formatNumber(data.total)} matches · ${PAGE_SIZE} per page · sorted by ${sort} ${
                  order === "desc" ? "(high → low)" : "(low → high)"
                }`
              : "Loading…"
          }
          titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
          subheaderTypographyProps={{ variant: "caption" }}
        />
        <CardContent sx={{ pt: 0 }}>
          {loading ? (
            <IgListSkeleton rows={6} rowHeight={44} />
          ) : items.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No universities matched.
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>#</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sort === UNI_SORT_KEYS.variants}
                        direction={sort === UNI_SORT_KEYS.variants ? order : "desc"}
                        onClick={() => handleSortClick(UNI_SORT_KEYS.variants)}
                      >
                        University
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <TableSortLabel
                        active={sort === UNI_SORT_KEYS.users}
                        direction={sort === UNI_SORT_KEYS.users ? order : "desc"}
                        onClick={() => handleSortClick(UNI_SORT_KEYS.users)}
                      >
                        Users
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>Platform</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <TableSortLabel
                        active={sort === UNI_SORT_KEYS.subscribers}
                        direction={sort === UNI_SORT_KEYS.subscribers ? order : "desc"}
                        onClick={() => handleSortClick(UNI_SORT_KEYS.subscribers)}
                      >
                        Subscribed
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <TableSortLabel
                        active={sort === UNI_SORT_KEYS.linked}
                        direction={sort === UNI_SORT_KEYS.linked ? order : "desc"}
                        onClick={() => handleSortClick(UNI_SORT_KEYS.linked)}
                      >
                        Linked
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>Country</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      Latest signup
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((row, idx) => {
                    const rank = (data?.page - 1) * PAGE_SIZE + idx + 1;
                    const subscribePct =
                      row.user_count > 0
                        ? Math.round((row.subscribed_count / row.user_count) * 100)
                        : 0;
                    const variantsTitle = (row.variants || []).join("\n");
                    return (
                      <TableRow
                        key={row.key || row.name}
                        hover
                        sx={{ "&:last-child td": { borderBottom: 0 } }}
                      >
                        <TableCell sx={{ width: 40, color: "text.secondary" }}>
                          {rank}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box sx={{ minWidth: 0 }}>
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: 280,
                                  }}
                                  title={row.name}
                                >
                                  {row.name}
                                </Typography>
                                {row.canonical_id ? (
                                  <Tooltip
                                    title={
                                      row.canonical_location
                                        ? `Linked university · ${row.canonical_location}`
                                        : "Linked to a canonical University record"
                                    }
                                  >
                                    <VerifiedIcon
                                      sx={{ fontSize: 16, color: "info.main" }}
                                    />
                                  </Tooltip>
                                ) : null}
                              </Stack>
                              {row.variants_count > 1 ? (
                                <Tooltip title={variantsTitle} placement="bottom-start">
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ cursor: "help" }}
                                  >
                                    {row.variants_count} spellings merged
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  1 spelling
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                          {formatNumber(row.user_count)}
                        </TableCell>
                        <TableCell>
                          <PlatformBar
                            ios={row.ios_count}
                            android={row.android_count}
                            total={row.user_count}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Box sx={{ minWidth: 80 }}>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="flex-end"
                              alignItems="baseline"
                            >
                              <Typography variant="body2" fontWeight={600}>
                                {formatNumber(row.subscribed_count)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ({subscribePct}%)
                              </Typography>
                            </Stack>
                            <LinearProgress
                              variant="determinate"
                              value={subscribePct}
                              color="success"
                              sx={{
                                height: 4,
                                borderRadius: 999,
                                mt: 0.5,
                                bgcolor:
                                  theme.palette.mode === "dark"
                                    ? alpha(theme.palette.common.white, 0.08)
                                    : alpha(theme.palette.common.black, 0.06),
                                "& .MuiLinearProgress-bar": { borderRadius: 999 },
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {row.linked_count > 0 ? (
                            <Chip
                              size="small"
                              label={`${row.linked_count} / ${row.user_count}`}
                              variant="outlined"
                              color="info"
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Typography variant="body2" color="text.secondary">
                            {row.top_country || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(row.latest_signup_at)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!loading && items.length > 0 && totalPages > 1 ? (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
              sx={{ pt: 2 }}
            >
              <Typography variant="caption" color="text.secondary">
                Showing {(data.page - 1) * PAGE_SIZE + 1}–
                {Math.min(data.page * PAGE_SIZE, data.total)} of {formatNumber(data.total)}
              </Typography>
              <Pagination
                count={totalPages}
                page={data.page}
                onChange={(_e, p) => setPage(p)}
                size="small"
                color="primary"
                showFirstButton
                showLastButton
              />
            </Stack>
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
}
